import { useEffect, useMemo, useState } from 'react';
import { generateClient } from 'aws-amplify/data';
import { apiPost } from '../lib/api';
import { apiGet } from '../lib/api';
import type { Schema } from '../../amplify/data/resource';
import {
  Button, Input, Modal, Card, StatusBadge,
  SectionHeader, EmptyState, Spinner, useToast,
} from './ui';

type Client   = Schema['Client']['type'];
type Audience = Schema['Audience']['type'];
type Tab = 'overview' | 'clients' | 'audiences' | 'pixel' | 'contacts' | 'leads' | 'help';

const BLANK = {
  clientName:              '',
  metaAdAccountId:         '',
  metaPixelId:             '',
  metaCapiTokenArn:        '',
  metaPageId:              '',
  metaLeadFormId:          '',
  metaPageAccessToken:     '',
  notificationEmail:       '',
  notificationPhone:       '',
  retellEnabled:           'false',
  hightouchSyncIdPixel:    '',
  hightouchSyncIdProspeo:  '',
  hightouchSyncIdGoogle:   '',
  hightouchSyncIdLinkedin: '',
  hightouchSyncIdTiktok:   '',
  hightouchSyncIdTwitter:  '',
  googleCustomerId:        '',
  googleCmEligible:        'false',
  linkedinAccountId:       '',
  tiktokAdvertiserId:      '',
  tiktokPixelId:           '',
  twitterAccountId:        '',
  twitterPixelId:          '',
  contactEmail:            '',
  website:                 '',
  industry:                '',
  notes:                   '',
  clientType:              '',
  balanceDue:              '',
  billingStatus:           'current',
};

export default function ClientManager({ onNavigate }: { onNavigate?: (tab: Tab) => void }) {
  const db = useMemo(() => generateClient<Schema>(), []);
  const [clients,   setClients]   = useState<Client[]>([]);
  const [audiences,  setAudiences] = useState<Audience[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [open,     setOpen]     = useState(false);
  const [editing,  setEditing]  = useState<Client | null>(null);
  const [form,     setForm]     = useState(BLANK);
  const [saving,   setSaving]   = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [htSetup,        setHtSetup]        = useState<{ clientId: string } | null>(null);
  const [htSetupProspeo, setHtSetupProspeo] = useState<{ clientId: string } | null>(null);
  const [htSetupGoogle,  setHtSetupGoogle]  = useState<{ clientId: string } | null>(null);
  const [googleHtError,  setGoogleHtError]  = useState<Record<string, string>>({});
  const [htSetupTiktok,   setHtSetupTiktok]   = useState<{ clientId: string } | null>(null);
  const [tiktokHtError,   setTiktokHtError]   = useState<Record<string, string>>({});
  const [htSetupTwitter,  setHtSetupTwitter]  = useState<{ clientId: string } | null>(null);
  const [twitterHtError,  setTwitterHtError]  = useState<Record<string, string>>({});
  const [manualSyncId,   setManualSyncId]   = useState<Record<string, string>>({});
  const [savingManual,   setSavingManual]   = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [instrStep,  setInstrStep]  = useState<string | null>(null);
  const [billModal,  setBillModal]  = useState<Client | null>(null);
  const [billAmount, setBillAmount] = useState('');
  const [billDesc,   setBillDesc]   = useState('');
  const [billEmail,  setBillEmail]  = useState(true);
  const [billSms,    setBillSms]    = useState(false);
  const [billPhone,  setBillPhone]  = useState('');
  const [billSending,setBillSending]= useState(false);
  const [onboardModal,   setOnboardModal]   = useState<Client | null>(null);
  const [onboardPlan,    setOnboardPlan]    = useState('');
  const [onboardPlats,   setOnboardPlats]   = useState('');
  const [onboardBudget,  setOnboardBudget]  = useState('');
  const [onboardSending,    setOnboardSending]    = useState(false);
  const [autoChargeModal,   setAutoChargeModal]   = useState<Client | null>(null);
  const [autoChargeSpend,   setAutoChargeSpend]   = useState('');
  const [autoChargeLoading, setAutoChargeLoading] = useState(false);
  const [autoChargeResult,  setAutoChargeResult]  = useState<{ ok: boolean; msg: string } | null>(null);
  const [showAdvPlatforms,  setShowAdvPlatforms]  = useState(false);

  // Trial subscription modal state
  const [trialSubModal,   setTrialSubModal]   = useState<Client | null>(null);
  const [trialSubPlan,    setTrialSubPlan]    = useState<'monthly' | 'weekly'>('monthly');
  const [trialSubUrl,     setTrialSubUrl]     = useState<string | null>(null);
  const [trialSubLoading, setTrialSubLoading] = useState(false);

  // Audience segment state
  const [segmentsByClient, setSegmentsByClient] = useState<Record<string, any[]>>({});
  const [segmentModal, setSegmentModal] = useState<{ clientId: string; clientName: string } | null>(null);
  const [newSegName, setNewSegName] = useState('');
  const [newSegDesc, setNewSegDesc] = useState('');
  const [savingSeg,  setSavingSeg]  = useState(false);
  const { show, ToastEl }           = useToast();

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const [clientRes, audienceRes] = await Promise.all([
        db.models.Client.list(),
        db.models.Audience.list(),
      ]);
      if (clientRes.errors?.length)   throw new Error(clientRes.errors.map((e: any) => e.message).join('; '));
      if (audienceRes.errors?.length) throw new Error(audienceRes.errors.map((e: any) => e.message).join('; '));
      setClients((clientRes.data ?? []).sort((a, b) =>
        new Date(b.createdAt ?? '').getTime() - new Date(a.createdAt ?? '').getTime()
      ));
      setAudiences(audienceRes.data ?? []);
    } catch (e: any) {
      show('Failed to load clients: ' + e.message, 'error');
    } finally {
      setLoading(false);
    }
  }

  async function loadSegmentsForClient(clientId: string) {
    try {
      const res = await apiGet<{ segments: any[] }>(`/segment/list?clientId=${encodeURIComponent(clientId)}`);
      setSegmentsByClient(p => ({ ...p, [clientId]: res.segments ?? [] }));
    } catch { /* non-fatal */ }
  }

  async function handleCreateSegment() {
    if (!newSegName.trim() || !segmentModal) return;
    setSavingSeg(true);
    try {
      await apiPost('/segment/create', { clientId: segmentModal.clientId, segmentName: newSegName.trim(), description: newSegDesc.trim() });
      show(`Segment "${newSegName.trim()}" created`);
      await loadSegmentsForClient(segmentModal.clientId);
      setSegmentModal(null);
      setNewSegName('');
      setNewSegDesc('');
    } catch (e: any) {
      show('Failed to create segment: ' + e.message, 'error');
    } finally {
      setSavingSeg(false);
    }
  }

  async function handleDeleteSegment(clientId: string, segmentId: string, name: string) {
    if (!confirm(`Delete segment "${name}"? This can only be done for segments that were never activated or billed.`)) return;
    try {
      await apiPost('/segment/delete', { segmentId });
      show('Segment deleted');
      setSegmentsByClient(p => ({ ...p, [clientId]: (p[clientId] ?? []).filter(s => s.segmentId !== segmentId) }));
    } catch (e: any) {
      show('Failed: ' + e.message, 'error');
    }
  }

  async function handleDisableSegment(clientId: string, segmentId: string, name: string) {
    if (!confirm(`Disable segment "${name}"? It will be hidden from the client but billing history is preserved. You can re-enable it later.`)) return;
    try {
      await apiPost('/segment/disable', { segmentId });
      show('Segment disabled');
      setSegmentsByClient(p => ({
        ...p,
        [clientId]: (p[clientId] ?? []).map(s => s.segmentId === segmentId ? { ...s, status: 'cancelled' } : s),
      }));
    } catch (e: any) {
      show('Failed: ' + e.message, 'error');
    }
  }

  async function handlePauseSegment(clientId: string, segmentId: string, currentStatus: string) {
    const newStatus = currentStatus === 'paused' ? 'active'
      : currentStatus === 'cancelled' ? 'pending_setup'
      : 'paused';
    try {
      await apiPost('/segment/update', { segmentId, status: newStatus });
      setSegmentsByClient(p => ({
        ...p,
        [clientId]: (p[clientId] ?? []).map(s => s.segmentId === segmentId ? { ...s, status: newStatus } : s),
      }));
    } catch (e: any) {
      show('Failed: ' + e.message, 'error');
    }
  }

  function openCreate() {
    setEditing(null);
    setForm(BLANK);
    setOpen(true);
  }

  function openEdit(c: Client) {
    setEditing(c);
    setForm({
      clientName:              c.clientName              ?? '',
      metaAdAccountId:         c.metaAdAccountId         ?? '',
      metaPixelId:             c.metaPixelId             ?? '',
      metaCapiTokenArn:        c.metaCapiTokenArn        ?? '',
      metaPageId:              (c as any).metaPageId             ?? '',
      metaLeadFormId:          (c as any).metaLeadFormId         ?? '',
      metaPageAccessToken:     (c as any).metaPageAccessToken    ?? '',
      notificationEmail:       (c as any).notificationEmail      ?? '',
      notificationPhone:       (c as any).notificationPhone      ?? '',
      retellEnabled:           String((c as any).retellEnabled ?? false),
      hightouchSyncIdPixel:    c.hightouchSyncIdPixel    ?? '',
      hightouchSyncIdProspeo:  c.hightouchSyncIdProspeo  ?? '',
      hightouchSyncIdGoogle:   (c as any).hightouchSyncIdGoogle   ?? '',
      hightouchSyncIdLinkedin: (c as any).hightouchSyncIdLinkedin ?? '',
      hightouchSyncIdTiktok:   (c as any).hightouchSyncIdTiktok   ?? '',
      hightouchSyncIdTwitter:  (c as any).hightouchSyncIdTwitter  ?? '',
      googleCustomerId:        (c as any).googleCustomerId        ?? '',
      googleCmEligible:        (c as any).googleCmEligible        ?? 'false',
      linkedinAccountId:       (c as any).linkedinAccountId       ?? '',
      tiktokAdvertiserId:      (c as any).tiktokAdvertiserId      ?? '',
      tiktokPixelId:           (c as any).tiktokPixelId           ?? '',
      twitterAccountId:        (c as any).twitterAccountId        ?? '',
      twitterPixelId:          (c as any).twitterPixelId          ?? '',
      contactEmail:            c.contactEmail            ?? '',
      website:                 c.website                 ?? '',
      industry:                c.industry                ?? '',
      notes:                   c.notes                   ?? '',
      clientType:              (c as any).clientType     ?? '',
      balanceDue:              (c as any).balanceDue     ?? '',
      billingStatus:           (c as any).billingStatus  ?? 'current',
    });
    setOpen(true);
  }

  async function handleSave() {
    if (!form.clientName.trim()) { show('Client name required', 'error'); return; }
    if (!form.clientType)       { show('Business type is required', 'error'); return; }

    setSaving(true);
    try {
      if (editing) {
        const { googleCmEligible: _gcm2, retellEnabled: _re2, ...updateFields } = form;
        await db.models.Client.update({ id: editing.id, ...updateFields });

        // Also persist the new platform fields directly to DynamoDB via REST.
        // These fields are pending schema deployment so the GraphQL mutation
        // above silently drops them — the REST path writes them directly.
        const platformFields: Record<string, string> = {};
        const platformKeys = [
          'googleCustomerId', 'googleCmEligible', 'linkedinAccountId',
          'tiktokAdvertiserId', 'tiktokPixelId',
          'twitterAccountId', 'twitterPixelId',
          'hightouchSyncIdGoogle', 'hightouchSyncIdLinkedin',
          'hightouchSyncIdTiktok', 'hightouchSyncIdTwitter',
          'metaPageId', 'metaLeadFormId', 'metaPageAccessToken', 'notificationEmail', 'notificationPhone',
          'retellEnabled', 'clientType', 'balanceDue', 'billingStatus',
        ] as const;
        for (const key of platformKeys) {
          const val = form[key as keyof typeof form];
          if (typeof val === 'string') platformFields[key] = val;
        }
        if (Object.keys(platformFields).length > 0) {
          await apiPost('/onboard', { action: 'admin-update', clientId: editing.id, fields: platformFields });
        }

        show('Client updated');
      } else {
        // Auto-generate onboarding token on create
        const token = crypto.randomUUID();
        // googleCmEligible and retellEnabled are string in form state — strip before GraphQL create
        const { googleCmEligible: _gcm, retellEnabled: _re, ...schemaFields } = form;
        const created = await db.models.Client.create({ ...schemaFields, onboardingToken: token });
        if (created.errors?.length) {
          throw new Error(created.errors.map((e: any) => e.message).join('; '));
        }
        // Optimistically add to state immediately so it shows up before DynamoDB
        // eventual consistency catches up with the list query
        if (created.data) {
          setClients(prev => [created.data as Client, ...prev]);
        }
        // Auto-send invite email if contactEmail is set
        if (form.contactEmail && created.data?.id) {
          try {
            await apiPost('/onboard', { action: 'send-invite', clientId: created.data.id });
            show(`Client created — invite sent to ${form.contactEmail}`);
          } catch {
            show('Client created (invite email failed — check SES)', 'error');
          }
        } else {
          show('Client created');
        }
      }
      setOpen(false);
      // Refresh in background to sync any server-side defaults
      load();
    } catch (e: any) {
      show('Save failed: ' + e.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleSetupHightouch(c: Client) {
    setHtSetup({ clientId: c.id });
    try {
      const res = await apiPost<{ syncId: string; clientName: string }>('/hightouch/setup', { clientId: c.id, type: 'pixel' });
      show(`✓ Hightouch pixel sync #${res.syncId} created for ${res.clientName}!`);
      setHtSetup(null);
      await load();
    } catch (e: any) {
      show('Hightouch setup failed: ' + e.message, 'error');
      setHtSetup(null);
    }
  }

  async function handleSetupHightouchProspeo(c: Client) {
    setHtSetupProspeo({ clientId: c.id });
    try {
      const res = await apiPost<{ syncId: string; clientName: string }>('/hightouch/setup', { clientId: c.id, type: 'prospeo' });
      show(`✓ Hightouch Prospeo sync #${res.syncId} created for ${res.clientName}!`);
      setHtSetupProspeo(null);
      await load();
    } catch (e: any) {
      show('Hightouch Prospeo setup failed: ' + e.message, 'error');
      setHtSetupProspeo(null);
    }
  }

  async function handleSetupHightouchGoogle(c: Client) {
    setHtSetupGoogle({ clientId: c.id });
    setGoogleHtError(p => { const n = { ...p }; delete n[c.id]; return n; });
    try {
      const res = await apiPost<{ syncId: string; clientName: string }>('/hightouch/setup', { clientId: c.id, type: 'google' });
      show(`✓ Hightouch Google sync #${res.syncId} created for ${res.clientName}!`);
      setHtSetupGoogle(null);
      await load();
    } catch (e: any) {
      setGoogleHtError(p => ({ ...p, [c.id]: e.message }));
      setHtSetupGoogle(null);
    }
  }

  async function handleSetupHightouchTiktok(c: Client) {
    setHtSetupTiktok({ clientId: c.id });
    setTiktokHtError(p => { const n = { ...p }; delete n[c.id]; return n; });
    try {
      const res = await apiPost<{ syncId: string; clientName: string }>('/hightouch/setup', { clientId: c.id, type: 'tiktok' });
      show(`✓ Hightouch TikTok sync #${res.syncId} created for ${res.clientName}!`);
      setHtSetupTiktok(null);
      await load();
    } catch (e: any) {
      setTiktokHtError(p => ({ ...p, [c.id]: e.message }));
      setHtSetupTiktok(null);
    }
  }

  async function handleSetupHightouchTwitter(c: Client) {
    setHtSetupTwitter({ clientId: c.id });
    setTwitterHtError(p => { const n = { ...p }; delete n[c.id]; return n; });
    try {
      const res = await apiPost<{ syncId: string; clientName: string }>('/hightouch/setup', { clientId: c.id, type: 'twitter' });
      show(`✓ Hightouch X Ads sync #${res.syncId} created for ${res.clientName}!`);
      setHtSetupTwitter(null);
      await load();
    } catch (e: any) {
      setTwitterHtError(p => ({ ...p, [c.id]: e.message }));
      setHtSetupTwitter(null);
    }
  }

  async function handleSaveManualSyncId(c: Client) {
    const val = (manualSyncId[c.id] ?? '').trim();
    if (!val) return;
    setSavingManual(c.id);
    try {
      await apiPost('/onboard', { action: 'admin-update', clientId: c.id, fields: { hightouchSyncIdGoogle: val } });
      show(`✓ Sync ID ${val} saved for ${c.clientName}`);
      setManualSyncId(p => { const n = { ...p }; delete n[c.id]; return n; });
      setGoogleHtError(p => { const n = { ...p }; delete n[c.id]; return n; });
      await load();
    } catch (e: any) {
      show('Failed to save sync ID: ' + e.message, 'error');
    } finally {
      setSavingManual(null);
    }
  }

  async function handleSendLink(c: Client) {
    try {
      const token = crypto.randomUUID();
      await db.models.Client.update({ id: c.id, onboardingToken: token });
      const url = `${window.location.origin}/onboard?token=${token}`;
      await navigator.clipboard.writeText(url);
      // Also email it if contact email exists
      if (c.contactEmail) {
        try {
          await apiPost('/onboard', { action: 'send-invite', clientId: c.id });
          show(`Link copied + emailed to ${c.contactEmail}`);
        } catch {
          show('Link copied (email failed — check SES)');
        }
      } else {
        show('Onboarding link copied to clipboard!');
      }
    } catch (e: any) {
      show('Failed to generate link: ' + e.message, 'error');
    }
  }

  async function handleCopyLink(c: Client) {
    try {
      let token = c.onboardingToken;
      if (!token) {
        token = crypto.randomUUID();
        await db.models.Client.update({ id: c.id, onboardingToken: token });
      }
      const url = `${window.location.origin}/onboard?token=${token}`;
      await navigator.clipboard.writeText(url);
      show('Onboarding link copied!');
    } catch (e: any) {
      show('Failed to copy link: ' + e.message, 'error');
    }
  }

  async function handleCopyCreditsLink(c: Client) {
    try {
      const url = `https://intentflo.com/pricing/?clientId=${c.id}`;
      await navigator.clipboard.writeText(url);
      show('Credits link copied!');
    } catch (e: any) {
      show('Failed to copy link: ' + e.message, 'error');
    }
  }

  async function handleCopyAutopayLink(c: Client) {
    try {
      const res = await apiPost<{ url: string }>('/billing/setup-autopay', {
        clientId:   c.id,
        clientName: c.clientName,
        email:      c.contactEmail ?? undefined,
      });
      await navigator.clipboard.writeText(res.url);
      show('Autopay link copied!');
    } catch (e: any) {
      show('Failed to generate autopay link: ' + e.message, 'error');
    }
  }

  function openOnboardModal(c: Client) {
    setOnboardModal(c);
    setOnboardPlan('');
    setOnboardPlats('');
    setOnboardBudget('');
  }

  async function handleSendOnboarding() {
    if (!onboardModal?.contactEmail) return;
    setOnboardSending(true);
    try {
      await apiPost('/client/send-onboarding', {
        clientId:  onboardModal.id,
        toEmail:   onboardModal.contactEmail,
        toName:    onboardModal.clientName ?? '',
        plan:      onboardPlan,
        platforms: onboardPlats,
        budget:    onboardBudget,
        website:   (onboardModal as any).website ?? '',
      });
      show(`Onboarding email sent to ${onboardModal.contactEmail}`);
      setOnboardModal(null);
    } catch (e: any) {
      show('Failed to send onboarding email: ' + e.message, 'error');
    } finally {
      setOnboardSending(false);
    }
  }

  function openBillModal(c: Client) {
    setBillModal(c);
    setBillAmount((c as any).balanceDue ?? '');
    setBillDesc(`IntentFlo — Services ${new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' })}`);
    setBillEmail(true);
    setBillSms(false);
    setBillPhone('');
  }

  function openAutoChargeModal(c: Client) {
    setAutoChargeModal(c);
    setAutoChargeSpend((c as any).monthlyAdSpend ?? '');
    setAutoChargeResult(null);
  }

  async function handleAutoCharge() {
    if (!autoChargeModal) return;
    const monthlyMin  = parseFloat((autoChargeModal as any).monthlyMin ?? '399');
    const actualSpend = parseFloat(autoChargeSpend);
    const calculated  = isNaN(actualSpend) ? monthlyMin : Math.max(actualSpend * 0.30, monthlyMin);
    const amount      = Math.round(calculated * 100) / 100;
    if (amount < 0.5) { show('Charge amount must be at least $0.50', 'error'); return; }
    setAutoChargeLoading(true);
    setAutoChargeResult(null);
    try {
      const res = await apiPost<{ invoiceId: string; status: string; hostedUrl?: string }>(
        '/billing/auto-charge',
        {
          clientId:      autoChargeModal.id,
          amountDollars: amount,
          description:   `IntentFlo Audience Marketplace — ${new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' })}`,
        },
      );
      const statusLabel = res.status === 'paid' ? '✅ Charged successfully' : `⏳ Invoice ${res.status}`;
      setAutoChargeResult({ ok: true, msg: `${statusLabel} — $${amount.toFixed(2)} (invoice ${res.invoiceId})` });
      await db.models.Client.update({ id: autoChargeModal.id, ...{ balanceDue: '0', billingStatus: 'paid' } } as any);
      await load();
    } catch (e: any) {
      setAutoChargeResult({ ok: false, msg: e.message ?? 'Charge failed' });
    } finally {
      setAutoChargeLoading(false);
    }
  }

  async function handleSendBill() {
    if (!billModal) return;
    const amount = parseFloat(billAmount);
    if (isNaN(amount) || amount <= 0) { show('Enter a valid amount', 'error'); return; }
    if (amount < 0.50) { show('Minimum invoice amount is $0.50', 'error'); return; }
    setBillSending(true);
    try {
      const res = await apiPost<{ url: string; errors?: string[] }>('/billing/send-bill', {
        clientId:      billModal.id,
        amountDollars: amount,
        description:   billDesc || 'IntentFlo — Balance Due',
        sendEmail:     billEmail,
        recipientEmail: billEmail ? (billModal.contactEmail ?? '') : undefined,
        recipientName:  billEmail ? (billModal.clientName ?? '') : undefined,
        sendSms:       billSms,
        recipientPhone: billSms ? billPhone : undefined,
      });
      if (res.errors?.length) {
        show(`Bill sent (with warnings: ${res.errors.join(', ')})`, 'error');
      } else {
        show(`Bill sent to ${billModal.clientName}!`);
      }
      setBillModal(null);
      // Update balanceDue on client record
      await db.models.Client.update({ id: billModal.id, ...{ balanceDue: String(amount), billingStatus: 'past_due' } } as any);
      await load();
    } catch (e: any) {
      show('Failed to send bill: ' + e.message, 'error');
    } finally {
      setBillSending(false);
    }
  }

  async function handleToggleActive(c: Client) {
    try {
      await db.models.Client.update({ id: c.id, active: !c.active });
      show(`Client ${c.active ? 'deactivated' : 'activated'}`);
      await load();
    } catch (e: any) {
      show('Failed: ' + e.message, 'error');
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this client and all associated data?')) return;
    setDeleting(id);
    try {
      await db.models.Client.delete({ id });
      show('Client deleted');
      await load();
    } catch (e: any) {
      show('Delete failed: ' + e.message, 'error');
    } finally {
      setDeleting(null);
    }
  }

  async function handleMarkTos(c: Client) {
    try {
      await db.models.Client.update({ id: c.id, tosAcceptedAt: new Date().toISOString() });
      show('Custom Audience TOS marked as accepted');
      await load();
    } catch (e: any) {
      show('Failed: ' + e.message, 'error');
    }
  }

  async function handleMarkPixel(c: Client) {
    try {
      await db.models.Client.update({ id: c.id, pixelInstalled: true });
      show('Pixel marked as installed');
      await load();
    } catch (e: any) {
      show('Failed: ' + e.message, 'error');
    }
  }

  async function handleMarkDomain(c: Client) {
    try {
      await db.models.Client.update({ id: c.id, domainVerified: true });
      show('Domain marked as verified');
      await load();
    } catch (e: any) {
      show('Failed: ' + e.message, 'error');
    }
  }

  async function handleMarkPartner(c: Client) {
    try {
      await db.models.Client.update({ id: c.id, partnerAccessGranted: true } as any);
      show('Partner access marked as granted');
      await load();
    } catch (e: any) {
      show('Failed: ' + e.message, 'error');
    }
  }

  async function handleMarkLeadAdForm(c: Client) {
    try {
      await db.models.Client.update({ id: c.id, leadAdFormCreated: true } as any);
      show('Lead Ad form marked as created');
      await load();
    } catch (e: any) {
      show('Failed: ' + e.message, 'error');
    }
  }

  async function handleGetTrialSubscriptionLink() {
    if (!trialSubModal) return;
    if (!trialSubModal.contactEmail) { show('No email on file — add one in Edit Client first', 'error'); return; }
    setTrialSubLoading(true);
    setTrialSubUrl(null);
    try {
      const plan = trialSubPlan === 'weekly' ? 'trial_weekly' : 'trial_monthly';
      const res  = await apiPost<{ url: string }>('/checkout/session', {
        plan,
        clientId: trialSubModal.id,
        email:    trialSubModal.contactEmail,
      });
      setTrialSubUrl(res.url);
      await navigator.clipboard.writeText(res.url);
      show('Payment link copied to clipboard!');
    } catch (e: any) {
      show('Failed to generate link: ' + e.message, 'error');
    } finally {
      setTrialSubLoading(false);
    }
  }

  const f = (k: keyof typeof BLANK) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(p => ({ ...p, [k]: e.target.value }));

  return (
    <div>
      <SectionHeader
        title="Clients"
        subtitle="Manage client accounts and their Meta Ad connections"
        action={<Button variant="primary" onClick={openCreate} icon="＋">Add Client</Button>}
      />

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <Spinner size={28} />
        </div>
      ) : clients.length === 0 ? (
        <EmptyState
          icon="⬡"
          title="No clients yet"
          description="Add your first client to start building and activating audiences."
          action={<Button variant="primary" onClick={openCreate}>Add your first client</Button>}
        />
      ) : (
        (() => {
          const trialClients   = clients.filter(c => (c as any).plan === 'test_campaign');
          const regularClients = clients.filter(c => (c as any).plan !== 'test_campaign');
          return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* ── Trial Users ────────────────────────────────────────────────── */}
          {trialClients.length > 0 && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#818cf8', letterSpacing: '0.04em' }}>⚡ TRIAL USERS</div>
                <div style={{ flex: 1, height: 1, background: 'rgba(129,140,248,0.2)' }} />
                <div style={{ fontSize: 11, color: '#4a5d7a' }}>{trialClients.length} trial{trialClients.length !== 1 ? 's' : ''}</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {trialClients.map(c => {
                  const trialStart   = new Date(c.createdAt ?? '');
                  const trialEnd     = new Date(trialStart.getTime() + 14 * 24 * 60 * 60 * 1000);
                  const now          = Date.now();
                  const daysLeft     = Math.ceil((trialEnd.getTime() - now) / (1000 * 60 * 60 * 24));
                  const trialExpired = daysLeft < 0;
                  const trialEnding  = !trialExpired && daysLeft <= 3;
                  const fmt          = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                  return (
                    <Card key={c.id} style={{ padding: 0, overflow: 'hidden' }}>
                      {trialExpired && (
                        <div style={{ background: 'rgba(239,68,68,0.1)', borderBottom: '1px solid rgba(239,68,68,0.2)', padding: '6px 20px', display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 13 }}>🔴</span>
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#f87171' }}>
                            Trial expired {Math.abs(daysLeft)} day{Math.abs(daysLeft) !== 1 ? 's' : ''} ago — send subscription link to continue services
                          </span>
                        </div>
                      )}
                      {trialEnding && (
                        <div style={{ background: 'rgba(251,191,36,0.08)', borderBottom: '1px solid rgba(251,191,36,0.2)', padding: '6px 20px', display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 13 }}>⚠️</span>
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#fbbf24' }}>
                            Trial ends in {daysLeft} day{daysLeft !== 1 ? 's' : ''} — send subscription link soon
                          </span>
                        </div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 20px', flexWrap: 'wrap' }}>
                        {/* Name / email */}
                        <div onClick={() => openEdit(c)} style={{ flex: '0 0 200px', minWidth: 150, cursor: 'pointer' }}>
                          <div style={{ fontWeight: 600, color: '#e8edf5', marginBottom: 2 }}>{c.clientName}</div>
                          {c.contactEmail && <div style={{ fontSize: 11, color: '#4a5d7a' }}>{c.contactEmail}</div>}
                          <div style={{ marginTop: 6, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', padding: '2px 7px', borderRadius: 4, background: 'rgba(129,140,248,0.15)', color: '#818cf8' }}>14-DAY TRIAL</span>
                            <StatusBadge status={c.active ? 'active' : 'inactive'} />
                          </div>
                        </div>
                        {/* Trial dates */}
                        <div style={{ flex: 1, minWidth: 200 }}>
                          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 8 }}>
                            <div>
                              <div style={{ fontSize: 10, color: '#4a5d7a', fontWeight: 600, letterSpacing: '0.06em', marginBottom: 2 }}>TRIAL START</div>
                              <div style={{ fontSize: 13, color: '#94a3b8' }}>{fmt(trialStart)}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: 10, color: '#4a5d7a', fontWeight: 600, letterSpacing: '0.06em', marginBottom: 2 }}>TRIAL END</div>
                              <div style={{ fontSize: 13, color: trialExpired ? '#ef4444' : trialEnding ? '#f59e0b' : '#94a3b8' }}>{fmt(trialEnd)}</div>
                            </div>
                            {!trialExpired && (
                              <div style={{ alignSelf: 'center' }}>
                                <span style={{
                                  fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 12,
                                  background: trialEnding ? 'rgba(251,191,36,0.12)' : 'rgba(16,185,129,0.12)',
                                  color:      trialEnding ? '#fbbf24'               : '#34d399',
                                  border:     `1px solid ${trialEnding ? 'rgba(251,191,36,0.3)' : 'rgba(16,185,129,0.3)'}`,
                                }}>
                                  {daysLeft === 0 ? 'Last day' : `${daysLeft}d left`}
                                </span>
                              </div>
                            )}
                          </div>
                          <div style={{ fontSize: 12, color: '#4a5d7a' }}>
                            Status: <span style={{ color: '#94a3b8' }}>{(c as any).onboardingStatus || 'Paid - Onboarding Pending'}</span>
                          </div>
                        </div>
                        {/* Actions */}
                        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                          <Button size="sm" variant="ghost"
                            onClick={() => { setTrialSubModal(c); setTrialSubUrl(null); setTrialSubPlan('monthly'); }}
                            style={{ color: '#818cf8', borderColor: 'rgba(129,140,248,0.3)', fontWeight: 600 }}>
                            💳 Send Sub Link
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openEdit(c)}>Edit</Button>
                          <Button size="sm" variant="danger" loading={deleting === c.id} onClick={() => handleDelete(c.id)}>Del</Button>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </>
          )}

          {/* ── Clients divider (only when trial users also exist) ──────────── */}
          {trialClients.length > 0 && regularClients.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#60a5fa', letterSpacing: '0.04em' }}>CLIENTS</div>
              <div style={{ flex: 1, height: 1, background: 'rgba(59,130,246,0.2)' }} />
              <div style={{ fontSize: 11, color: '#4a5d7a' }}>{regularClients.length} client{regularClients.length !== 1 ? 's' : ''}</div>
            </div>
          )}

          {/* ── Client Cards ───────────────────────────────────────────────── */}
          {regularClients.map(c => {
            const isExpanded  = expandedId === c.id;
            const iKey        = (s: string) => `${c.id}:${s}`;
            const toggleInstr = (s: string) => setInstrStep(p => p === iKey(s) ? null : iKey(s));
            const instrOpen   = (s: string) => instrStep === iKey(s);

            const steps: {
              key: string; num: number; label: string; detail: string;
              done: boolean; who: 'YOU' | 'CLIENT' | 'BOTH'; blocked?: boolean;
              why: string;
            }[] = [
              {
                num: 1, key: 'onboarding', who: 'YOU',
                label: 'Send onboarding link',
                why: 'Client fills in their Meta Ad Account ID + Pixel ID via a secure form.',
                detail: c.onboardingToken
                  ? (c.onboardingCompletedAt ? '✓ Completed by client' : 'Link sent — awaiting client')
                  : 'Not sent yet',
                done: !!c.onboardingToken,
              },
              {
                num: 2, key: 'meta', who: 'CLIENT',
                label: 'Meta credentials received',
                why: 'You need the Ad Account ID and Pixel ID to run Custom Audiences + CAPI.',
                detail: (c.metaAdAccountId && c.metaPixelId)
                  ? `Act: ${c.metaAdAccountId} · Pixel: ${c.metaPixelId}`
                  : 'Waiting for client to complete onboarding',
                done: !!c.metaAdAccountId && !!c.metaPixelId,
                blocked: !c.onboardingToken,
              },
              {
                num: 3, key: 'partner', who: 'CLIENT',
                label: 'Grant you partner access to their ad account',
                why: 'Lets you run campaigns inside their Meta ad account — ads come from their brand, their page, their billing. You manage everything on their behalf.',
                detail: (c as any).partnerAccessGranted
                  ? '✓ Partner access granted'
                  : c.metaAdAccountId
                    ? `Client goes to Business Settings → Partners → Add → enter your Business Manager ID`
                    : 'Waiting for Ad Account ID (step 2)',
                done: !!(c as any).partnerAccessGranted,
                blocked: !c.metaAdAccountId,
              },
              {
                num: 4, key: 'pixel', who: 'CLIENT',
                label: 'Pixel installed on site',
                why: 'The tag captures visitor events — page views and form submits — to build your seed audience.',
                detail: c.pixelInstalled ? '✓ Confirmed installed' : 'Awaiting installation',
                done: !!c.pixelInstalled,
                blocked: !c.metaAdAccountId,
              },
              {
                num: 5, key: 'tos', who: 'YOU',
                label: 'Accept Custom Audience TOS',
                why: 'Meta requires the ad account owner to accept ToS before any Custom Audience syncs will activate.',
                detail: c.tosAcceptedAt ? '✓ Accepted' : 'Must be accepted in Meta Business Manager',
                done: !!c.tosAcceptedAt,
                blocked: !c.metaAdAccountId,
              },
              {
                num: 6, key: 'domain', who: 'YOU',
                label: 'Add trusted domain in Meta',
                why: 'Meta requires the client\'s website domain to be verified in Business Manager. Without it, ads may be blocked from publishing without peer review, and pixel events may be unattributed.',
                detail: (c as any).domainVerified
                  ? '✓ Domain verified'
                  : c.website
                    ? `Add ${c.website.replace(/^https?:\/\//, '').replace(/\/$/, '')} to Meta Business Portfolio → Brand Safety → Domains`
                    : 'Set the client website URL first (Edit client)',
                done: !!(c as any).domainVerified,
                blocked: !c.metaAdAccountId,
              },
              {
                num: 7, key: 'capi', who: 'BOTH',
                label: 'Store CAPI token',
                why: 'CAPI sends form submission events server-side to Meta — improves attribution and audience matching.',
                detail: c.metaCapiTokenArn ? `✓ ${c.metaCapiTokenArn}` : 'Not configured',
                done: !!c.metaCapiTokenArn,
                blocked: !c.metaAdAccountId,
              },
              {
                num: 8, key: 'audience', who: 'YOU',
                label: 'Create pixel audience',
                why: 'Defines the group of site visitors that Hightouch will sync to Meta as a Custom Audience.',
                detail: 'Go to Audiences tab → New Audience → Pixel (site visitors) → Seed (Lookalike)',
                done: audiences.some(a => a.clientId === c.id && a.source === 'pixel'),
                blocked: !c.pixelInstalled,
              },
              {
                num: 9, key: 'hightouch', who: 'YOU',
                label: 'Setup Hightouch sync',
                why: 'Pushes the pixel audience to Meta Custom Audiences automatically on a schedule.',
                detail: c.hightouchSyncIdPixel ? `✓ Sync #${c.hightouchSyncIdPixel}` : 'Not set up',
                done: !!c.hightouchSyncIdPixel,
                blocked: !c.pixelInstalled,
              },
              {
                num: 10, key: 'prospeo', who: 'YOU',
                label: 'Run Prospeo pipeline (optional)',
                why: 'Finds net-new contacts matching your ICP to supplement the pixel audience with cold outreach targets.',
                detail: 'Go to Pipeline tab → select client + audience → configure ICP filters → Run',
                done: false,
                blocked: !c.hightouchSyncIdPixel,
              },
              {
                num: 11, key: 'leadad', who: 'YOU',
                label: 'Create Meta Lead Ad form',
                why: 'Lead Ads capture contact info (name, email, phone) inside Meta — no landing page needed. As a partner manager you can build and launch this entirely on their behalf.',
                detail: !!(c as any).leadAdFormCreated ? '✓ Form created' : 'Ads Manager → Create campaign → Lead generation → Instant Form',
                done: !!(c as any).leadAdFormCreated,
                blocked: !(c as any).partnerAccessGranted,
              },
            ];

            // ── Google Ads steps (shown when googleCustomerId is saved) ───────────
            if ((c as any).googleCustomerId) {
              const cmEligible = (c as any).googleCmEligible === 'true';
              steps.push({
                num: steps.length + 1, key: 'google-hightouch', who: 'YOU',
                label: 'Setup Hightouch → Google Customer Match sync',
                why: `Auto-creates a Hightouch model + Customer Match sync targeting customer ID ${(c as any).googleCustomerId}. Runs on schedule to keep the audience fresh.`,
                detail: (c as any).hightouchSyncIdGoogle
                  ? `✓ Sync #${(c as any).hightouchSyncIdGoogle} running`
                  : cmEligible ? 'Click Setup below — takes ~10 seconds' : '⏳ Pending: account needs $50k+ lifetime Google Ads spend',
                done: !!(c as any).hightouchSyncIdGoogle,
                blocked: !(c as any).googleCustomerId,
              });
            }

            if ((c as any).tiktokAdvertiserId) {
              steps.push({
                num: steps.length + 1, key: 'tiktok-hightouch', who: 'YOU',
                label: 'Setup Hightouch → TikTok Custom Audience sync',
                why: `Auto-creates a Hightouch model + TikTok Custom Audience sync for advertiser ${(c as any).tiktokAdvertiserId}.`,
                detail: (c as any).hightouchSyncIdTiktok
                  ? `✓ Sync #${(c as any).hightouchSyncIdTiktok} running`
                  : 'Click Setup below — takes ~10 seconds',
                done: !!(c as any).hightouchSyncIdTiktok,
                blocked: !(c as any).tiktokAdvertiserId,
              });
            }

            if ((c as any).twitterAccountId) {
              steps.push({
                num: steps.length + 1, key: 'twitter-hightouch', who: 'YOU',
                label: 'Setup Hightouch → X Ads Custom Audience sync',
                why: `Auto-creates a Hightouch model + X Ads Custom Audience sync for account ${(c as any).twitterAccountId}.`,
                detail: (c as any).hightouchSyncIdTwitter
                  ? `✓ Sync #${(c as any).hightouchSyncIdTwitter} running`
                  : 'Click Setup below — takes ~10 seconds',
                done: !!(c as any).hightouchSyncIdTwitter,
                blocked: !(c as any).twitterAccountId,
              });
            }

            const doneCount = steps.filter(s => s.done).length;
            const allDone   = doneCount === steps.length;

            const isMarketplaceClient  = (c as any).plan === 'marketplace';
            const selectedAudiences   = isMarketplaceClient ? ((c as any).selectedAudiences ?? '') : '';
            const audienceList        = selectedAudiences ? selectedAudiences.split(',').map((s: string) => s.trim()).filter(Boolean) : [];
            const monthlyAdSpend      = (c as any).monthlyAdSpend ?? '';

            return (
              <Card key={c.id} style={{ padding: 0, overflow: 'hidden' }}>
                {/* Marketplace pending banner */}
                {isMarketplaceClient && !c.active && (
                  <div style={{ background: 'rgba(139,92,246,0.12)', borderBottom: '1px solid rgba(139,92,246,0.25)', padding: '6px 20px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13 }}>🎯</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#a78bfa' }}>Audience Marketplace — pending activation</span>
                    {monthlyAdSpend && <span style={{ fontSize: 11, color: '#7c3aed', marginLeft: 4 }}>Ad budget: ${monthlyAdSpend}/mo</span>}
                    <span style={{ fontSize: 11, color: '#6d28d9', marginLeft: 8 }}>Bill min: <strong style={{ color: '#a78bfa' }}>${(c as any).monthlyMin ?? '399'}/mo</strong>{(c as any).promoCode ? ` (promo: ${(c as any).promoCode})` : ''}</span>
                  </div>
                )}
                {/* Needs setup banner */}
                {!(c as any).clientType && !isMarketplaceClient && (
                  <div
                    onClick={() => openEdit(c)}
                    style={{ background: 'rgba(251,191,36,0.1)', borderBottom: '1px solid rgba(251,191,36,0.2)', padding: '6px 20px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
                    title="Click to complete client setup"
                  >
                    <span style={{ fontSize: 13 }}>⚠️</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#fbbf24' }}>Needs setup — click to complete</span>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 20px', flexWrap: 'wrap' }}>
                  <div
                    onClick={() => openEdit(c)}
                    style={{ flex: '0 0 200px', minWidth: 150, cursor: 'pointer' }}
                    title="Click to edit client"
                  >
                    <div style={{ fontWeight: 600, color: '#e8edf5', marginBottom: 2 }}>{c.clientName}</div>
                    {c.website      && <div style={{ fontSize: 11, color: '#4a5d7a' }}>{c.website}</div>}
                    {c.contactEmail && <div style={{ fontSize: 11, color: '#4a5d7a' }}>{c.contactEmail}</div>}
                    <div style={{ marginTop: 6, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      <StatusBadge status={c.active ? 'active' : 'inactive'} />
                      {isMarketplaceClient && (
                        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', padding: '2px 7px', borderRadius: 4, background: 'rgba(139,92,246,0.15)', color: '#a78bfa' }}>MARKETPLACE</span>
                      )}
                    </div>
                    {/* Selected audiences */}
                    {audienceList.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <div style={{ fontSize: 10, color: '#4a5d7a', fontWeight: 600, letterSpacing: '0.06em', marginBottom: 4 }}>SELECTED AUDIENCES</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {audienceList.map((a: string, i: number) => (
                            <span key={i} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 12, background: 'rgba(139,92,246,0.1)', color: '#c4b5fd', border: '1px solid rgba(139,92,246,0.2)' }}>{a}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div style={{ flex: 1, minWidth: 160 }}>
                    {!isMarketplaceClient && (
                      <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                      {steps.map(s => (
                        <div key={s.key} title={`${s.done ? '✓' : '✗'} ${s.label}`} style={{
                          width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                          background: s.done ? '#10b981' : '#1e3a5f',
                        }} />
                      ))}
                      <span style={{ fontSize: 12, fontWeight: 600, marginLeft: 4, color: allDone ? '#10b981' : '#4a5d7a' }}>
                        {allDone ? '✓ Fully live' : `${doneCount} / ${steps.length} complete`}
                      </span>
                    </div>
                    <button
                      onClick={() => { setExpandedId(p => p === c.id ? null : c.id); setInstrStep(null); }}
                      style={{
                        background: 'none', border: '1px solid #1a2840', borderRadius: 6,
                        color: '#4a5d7a', fontSize: 11, cursor: 'pointer', padding: '4px 10px',
                        fontFamily: 'Sora, sans-serif',
                      }}
                    >
                      {isExpanded ? '▴ Hide checklist' : '▾ Setup checklist'}
                    </button>
                      </>
                    )}
                    {isMarketplaceClient && (
                      <div style={{ fontSize: 12, color: '#64748b' }}>
                        {c.active
                          ? `✅ Active — bill min $${(c as any).monthlyMin ?? '399'}/mo${(c as any).promoCode ? ` (${(c as any).promoCode})` : ''}`
                          : '⏳ Review audience selections above, then activate when ad account is connected.'}
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <Button size="sm" variant="ghost" onClick={() => openOnboardModal(c)}
                      style={{ color: '#34d399', borderColor: 'rgba(52,211,153,0.3)' }}
                      title={c.contactEmail ? `Send onboarding to ${c.contactEmail}` : 'No email on file'}>
                      📧 Onboard
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openBillModal(c)}
                      style={{ color: '#fbbf24', borderColor: 'rgba(251,191,36,0.3)' }}>
                      🧾 Send Bill
                    </Button>
                    {isMarketplaceClient && (
                      <Button size="sm" variant="ghost" onClick={() => openAutoChargeModal(c)}
                        style={{ color: '#818cf8', borderColor: 'rgba(129,140,248,0.3)' }}>
                        💳 Charge
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => handleCopyAutopayLink(c)}
                      style={{ color: '#38bdf8', borderColor: 'rgba(56,189,248,0.3)' }}
                      title="Copy a Stripe link the client can use to save a card on file">
                      🔗 Autopay Link
                    </Button>
                    {isMarketplaceClient && !c.active ? (
                      <Button size="sm" variant="primary" onClick={() => handleToggleActive(c)}
                        style={{ background: '#10b981', borderColor: '#10b981', fontWeight: 700 }}>
                        ✅ Activate Client
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => handleToggleActive(c)}
                        style={{ color: c.active ? '#ef4444' : '#10b981' }}>
                        {c.active ? 'Pause' : 'Activate'}
                      </Button>
                    )}
                    <Button size="sm" variant="danger" loading={deleting === c.id} onClick={() => handleDelete(c.id)}>Del</Button>
                  </div>
                </div>

                {/* Expanded checklist */}
                {isExpanded && (
                  <div style={{ borderTop: '1px solid #0d1a2e', padding: '12px 20px 20px' }}>
                    <div style={{ fontSize: 11, color: '#3b4f6a', marginBottom: 14, letterSpacing: '0.05em', fontFamily: 'DM Mono, monospace' }}>
                      SETUP WORKFLOW — follow steps in order
                    </div>
                    {steps.map((s, i) => {
                      const isNext = !s.done && !s.blocked && steps.slice(0, i).every(prev => prev.done || prev.key === 'audience' || prev.key === 'prospeo');
                      const isOpen = instrOpen(s.key);
                      return (
                        <div key={s.key} style={{ marginBottom: 6 }}>
                          <div
                            onClick={() => !s.blocked && toggleInstr(s.key)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 12,
                              padding: '10px 14px', borderRadius: 9,
                              background: isNext ? 'rgba(37,99,235,0.08)' : s.done ? 'rgba(16,185,129,0.05)' : 'transparent',
                              border: isNext ? '1px solid rgba(59,130,246,0.25)' : s.done ? '1px solid rgba(16,185,129,0.12)' : '1px solid transparent',
                              cursor: s.blocked ? 'not-allowed' : 'pointer',
                              opacity: s.blocked ? 0.4 : 1,
                              transition: 'all 0.15s',
                            }}
                          >
                            {/* Step number / status */}
                            <div style={{
                              width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 11, fontWeight: 700,
                              background: s.done ? '#10b981' : isNext ? '#2563eb' : '#0d1a2e',
                              color: s.done || isNext ? '#fff' : '#3b4f6a',
                            }}>
                              {s.done ? '✓' : s.num}
                            </div>

                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 13, fontWeight: 600, color: s.done ? '#6ee7b7' : isNext ? '#e8edf5' : '#64748b' }}>
                                  {s.label}
                                </span>
                                {isNext && <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', color: '#3b82f6', background: 'rgba(59,130,246,0.15)', padding: '2px 6px', borderRadius: 4 }}>NEXT</span>}
                                {s.blocked && <span style={{ fontSize: 9, color: '#3b4f6a', letterSpacing: '0.08em' }}>🔒 BLOCKED</span>}
                              </div>
                              <div style={{ fontSize: 11, color: s.done ? '#10b981' : '#3b4f6a', marginTop: 2 }}>{s.detail}</div>
                            </div>

                            <span style={{
                              fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', padding: '2px 7px', borderRadius: 4, flexShrink: 0,
                              background: s.who === 'YOU' ? '#0c1a3d' : s.who === 'CLIENT' ? '#200a0a' : '#1a1500',
                              color:      s.who === 'YOU' ? '#60a5fa' : s.who === 'CLIENT' ? '#f87171' : '#fbbf24',
                            }}>{s.who}</span>

                            {!s.done && !s.blocked && (
                              <span style={{ fontSize: 11, color: isOpen ? '#f59e0b' : '#3b82f6', fontWeight: 600, flexShrink: 0 }}>
                                {isOpen ? '▴' : '▾'}
                              </span>
                            )}
                          </div>

                          {/* Action panel */}
                          {!s.blocked && isOpen && (
                            <div style={{ margin: '4px 0 8px 38px', background: '#060a12', border: '1px solid #1a2840', borderRadius: 9, padding: '14px 16px' }}>
                              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12, lineHeight: 1.6 }}>
                                <strong style={{ color: '#94a3b8' }}>Why this matters:</strong> {s.why}
                              </div>

                              {s.key === 'onboarding' && (
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                  <Button size="sm" variant="primary" onClick={() => handleCopyLink(c)}>
                                    📋 {c.onboardingToken ? 'Copy & Resend Link' : 'Generate & Send Link'}
                                  </Button>
                                  {c.contactEmail && <div style={{ fontSize: 11, color: '#4a5d7a', alignSelf: 'center' }}>→ sends to {c.contactEmail}</div>}
                                </div>
                              )}

                              {s.key === 'meta' && (
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                  <Button size="sm" variant="primary" onClick={() => handleCopyLink(c)}>📋 Resend Link</Button>
                                  <Button size="sm" variant="ghost" onClick={() => openEdit(c)}>✏️ Enter manually</Button>
                                </div>
                              )}

                              {s.key === 'partner' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                  <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.8 }}>
                                    <strong style={{ color: '#e2e8f0' }}>Client does (one-time):</strong><br />
                                    Meta Business Manager → Business Settings → <strong style={{ color: '#e2e8f0' }}>Partners</strong> → Add → enter your Business Manager ID.<br />
                                    Grant access to: <strong style={{ color: '#e2e8f0' }}>Ad account</strong> + <strong style={{ color: '#e2e8f0' }}>Pixel</strong> + <strong style={{ color: '#e2e8f0' }}>Page</strong> (all three).
                                  </div>
                                  <div style={{ padding: '8px 12px', background: '#060a12', border: '1px solid #1a2840', borderRadius: 7, fontSize: 12, color: '#64748b' }}>
                                    Once granted: switch to their ad account in Meta Ads Manager (top-left account switcher) — the Custom Audience is already there, ready to use in campaigns.
                                  </div>
                                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <a
                                      href="https://business.facebook.com/settings/partners"
                                      target="_blank" rel="noopener noreferrer"
                                      style={{ display: 'inline-block', padding: '6px 14px', background: '#1877f2', color: '#fff', borderRadius: 7, fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>
                                      Open Meta Partner Settings →
                                    </a>
                                    <Button size="sm" variant="ghost" onClick={() => handleMarkPartner(c)}>✓ Mark as granted</Button>
                                  </div>
                                </div>
                              )}

                              {s.key === 'pixel' && (
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                  <Button size="sm" variant="primary" onClick={() => onNavigate?.('pixel')}>
                                    ◉ Go to Pixel tab → copy snippet
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => handleMarkPixel(c)}>✓ Mark as installed</Button>
                                </div>
                              )}

                              {s.key === 'tos' && (
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                  {c.metaAdAccountId ? (
                                    <a href={`https://business.facebook.com/ads/manage/customaudiences/tos/?act=${c.metaAdAccountId.replace(/^act_/, '')}`}
                                      target="_blank" rel="noopener noreferrer"
                                      style={{ padding: '6px 14px', background: '#1877f2', color: '#fff', borderRadius: 7, fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>
                                      Open Meta TOS Page →
                                    </a>
                                  ) : (
                                    <div style={{ fontSize: 11, color: '#ef4444' }}>Complete step 2 first to get the Ad Account ID</div>
                                  )}
                                  <Button size="sm" variant="ghost" onClick={() => handleMarkTos(c)}>✓ Mark as accepted</Button>
                                </div>
                              )}

                              {s.key === 'domain' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                  <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.8 }}>
                                    <strong style={{ color: '#e2e8f0' }}>In Meta Business Manager:</strong><br />
                                    Business Settings → Brand Safety &amp; Suitability → Domains → <strong style={{ color: '#e2e8f0' }}>Add</strong>
                                    {c.website && (
                                      <> → type: <span style={{ fontFamily: 'DM Mono, monospace', color: '#60a5fa' }}>
                                        {c.website.replace(/^https?:\/\//, '').replace(/\/$/, '').split('/')[0]}
                                      </span></>)}
                                  </div>
                                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <a
                                      href="https://business.facebook.com/settings/owned-domains"
                                      target="_blank" rel="noopener noreferrer"
                                      style={{ display: 'inline-block', padding: '6px 14px', background: '#1877f2', color: '#fff', borderRadius: 7, fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>
                                      Open Meta Domain Settings →
                                    </a>
                                    <Button size="sm" variant="ghost" onClick={() => handleMarkDomain(c)}>✓ Mark as done</Button>
                                  </div>
                                  <div style={{ fontSize: 11, color: '#475569' }}>
                                    After adding, Meta will ask you to verify via a DNS TXT record or a meta-tag in the site's &lt;head&gt;. The client's developer needs to do this part.
                                  </div>
                                </div>
                              )}

                              {s.key === 'capi' && (
                                <>
                                  <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>
                                    <strong>Client does:</strong> Meta Events Manager → Dataset → Settings → Conversions API → Generate access token → send token to you.
                                  </div>
                                  {c.metaPixelId && (
                                    <a href={`https://business.facebook.com/events_manager2/list/dataset/${c.metaPixelId}/settings`}
                                      target="_blank" rel="noopener noreferrer"
                                      style={{ display: 'inline-block', marginBottom: 10, padding: '6px 14px', background: '#1877f2', color: '#fff', borderRadius: 7, fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>
                                      Open Meta Events Manager →
                                    </a>
                                  )}
                                  <div style={{ fontSize: 12, color: '#cbd5e1', margin: '8px 0 4px' }}>You do: store token in AWS Secrets Manager</div>
                                  <pre style={{ background: '#030608', border: '1px solid #0d1a2e', borderRadius: 6, padding: '8px 12px', fontSize: 11, color: '#60a5fa', fontFamily: 'DM Mono, monospace', whiteSpace: 'pre-wrap', margin: '0 0 10px', lineHeight: 1.6 }}>
{`aws secretsmanager create-secret \\
  --name "intentflow/meta-capi/${c.id}" \\
  --secret-string "PASTE_TOKEN_HERE"`}
                                  </pre>
                                  <div style={{ fontSize: 11, color: '#4a5d7a', marginBottom: 10 }}>
                                    Then edit client → CAPI Token field → enter: <span style={{ color: '#60a5fa', fontFamily: 'DM Mono, monospace' }}>intentflow/meta-capi/{c.id}</span>
                                  </div>
                                  <Button size="sm" variant="ghost" onClick={() => openEdit(c)}>✏️ Open Edit Client</Button>
                                </>
                              )}

                              {s.key === 'audience' && (
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                  <Button size="sm" variant="primary" onClick={() => onNavigate?.('audiences')}>
                                    ◎ Go to Audiences tab → New Audience
                                  </Button>
                                  <div style={{ fontSize: 11, color: '#4a5d7a' }}>Source: Pixel · Type: Seed (Lookalike)</div>
                                </div>
                              )}

                              {s.key === 'hightouch' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                  {htSetup?.clientId === c.id ? (
                                    <div style={{ color: '#4a5d7a', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <Spinner size={14} /> Creating Hightouch model &amp; sync…
                                    </div>
                                  ) : (
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                      <Button size="sm" style={{ color: '#f59e0b', borderColor: '#78350f' }} variant="ghost"
                                        onClick={() => handleSetupHightouch(c)}>
                                        ⚡ Setup Hightouch
                                      </Button>
                                      <div style={{ fontSize: 11, color: '#4a5d7a' }}>Auto-creates model + sync + field mappings in Hightouch</div>
                                    </div>
                                  )}
                                </div>
                              )}

                              {s.key === 'prospeo' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <Button size="sm" variant="primary" onClick={() => onNavigate?.('contacts')}>
                                      ↑ Go to Contacts → Import Apollo CSV
                                    </Button>
                                    <div style={{ fontSize: 11, color: '#4a5d7a' }}>Build ICP filters to find net-new lookalike contacts</div>
                                  </div>
                                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                    {htSetupProspeo?.clientId === c.id ? (
                                      <div style={{ color: '#4a5d7a', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Spinner size={14} /> Creating Hightouch Prospeo model &amp; sync…
                                      </div>
                                    ) : c.hightouchSyncIdProspeo ? (
                                      <div style={{ fontSize: 12, color: '#22c55e' }}>✓ Prospeo sync #{c.hightouchSyncIdProspeo} ready</div>
                                    ) : (
                                      <>
                                        <Button size="sm" style={{ color: '#f59e0b', borderColor: '#78350f' }} variant="ghost"
                                          onClick={() => handleSetupHightouchProspeo(c)}>
                                          ⚡ Setup Prospeo Sync
                                        </Button>
                                        <div style={{ fontSize: 11, color: '#4a5d7a' }}>Auto-creates Hightouch model + sync for Prospeo/manual audiences</div>
                                      </>
                                    )}
                                  </div>
                                </div>
                              )}

                              {s.key === 'google-hightouch' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                  <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>
                                    Auto-creates the Hightouch model (reads your S3 CSV) + Customer Match sync targeting account <span style={{ fontFamily: 'DM Mono, monospace', color: '#60a5fa' }}>{(c as any).googleCustomerId}</span>.
                                  </div>
                                  {htSetupGoogle?.clientId === c.id ? (
                                    <div style={{ color: '#4a5d7a', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <Spinner size={14} /> Creating Hightouch model &amp; sync for Google…
                                    </div>
                                  ) : (c as any).hightouchSyncIdGoogle ? (
                                    <div style={{ fontSize: 12, color: '#22c55e' }}>✓ Sync #{(c as any).hightouchSyncIdGoogle} active</div>
                                  ) : (c as any).googleCmEligible !== 'true' ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                      <div style={{ padding: '8px 12px', background: '#0d1a0d', border: '1px solid #14532d', borderRadius: 7, fontSize: 11, color: '#86efac', lineHeight: 1.7 }}>
                                        ⏳ <strong>Pending: $50k+ lifetime Google Ads spend required</strong><br />
                                        Google Customer Match is locked until the account reaches $50,000 in total lifetime spend. Once it qualifies, mark it eligible below and the Setup button will unlock.
                                      </div>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Button size="sm" variant="ghost" style={{ color: '#60a5fa', borderColor: '#1e3a5f' }}
                                          onClick={async () => {
                                            await apiPost('/onboard', { action: 'admin-update', clientId: c.id, fields: { googleCmEligible: 'true' } });
                                            await load();
                                          }}>
                                          ✓ Mark Customer Match eligible
                                        </Button>
                                        <span style={{ fontSize: 11, color: '#4a5d7a' }}>Click once the account hits $50k spend</span>
                                      </div>
                                    </div>
                                  ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                        <Button size="sm" style={{ color: '#f59e0b', borderColor: '#78350f' }} variant="ghost"
                                          onClick={() => handleSetupHightouchGoogle(c)}>
                                          ⚡ Setup Hightouch → Google
                                        </Button>
                                        <div style={{ fontSize: 11, color: '#4a5d7a' }}>Auto-creates model + Customer Match sync</div>
                                      </div>
                                      {googleHtError[c.id] && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                          <div style={{ padding: '8px 12px', background: '#1a0a00', border: '1px solid #7c2d12', borderRadius: 7, fontSize: 11, color: '#f97316', lineHeight: 1.6 }}>
                                            {googleHtError[c.id]}
                                            <br /><br />
                                            <strong>Workaround:</strong> Create the sync manually in{' '}
                                            <a href="https://app.hightouch.com/syncs" target="_blank" rel="noreferrer" style={{ color: '#60a5fa' }}>Hightouch → Syncs</a>,
                                            then paste the sync ID below.
                                          </div>
                                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                            <input
                                              placeholder="Hightouch sync ID"
                                              value={manualSyncId[c.id] ?? ''}
                                              onChange={e => setManualSyncId(p => ({ ...p, [c.id]: e.target.value }))}
                                              style={{ fontSize: 12, background: '#0d1a2e', border: '1px solid #1e3a5f', borderRadius: 5, color: '#e2e8f0', padding: '4px 8px', width: 150 }}
                                            />
                                            <Button size="sm" variant="ghost"
                                              style={{ color: '#22c55e', borderColor: '#14532d', opacity: savingManual === c.id ? 0.5 : 1 }}
                                              onClick={() => handleSaveManualSyncId(c)}
                                              disabled={savingManual === c.id || !(manualSyncId[c.id] ?? '').trim()}>
                                              {savingManual === c.id ? <Spinner size={12} /> : 'Save'}
                                            </Button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}

                              {s.key === 'tiktok-hightouch' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                  <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>
                                    Auto-creates the Hightouch model + TikTok Custom Audience sync for advertiser <span style={{ fontFamily: 'DM Mono, monospace', color: '#60a5fa' }}>{(c as any).tiktokAdvertiserId}</span>.
                                  </div>
                                  {htSetupTiktok?.clientId === c.id ? (
                                    <div style={{ color: '#4a5d7a', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <Spinner size={14} /> Creating Hightouch model &amp; sync for TikTok…
                                    </div>
                                  ) : (c as any).hightouchSyncIdTiktok ? (
                                    <div style={{ fontSize: 12, color: '#22c55e' }}>✓ Sync #{(c as any).hightouchSyncIdTiktok} active</div>
                                  ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                        <Button size="sm" style={{ color: '#f59e0b', borderColor: '#78350f' }} variant="ghost"
                                          onClick={() => handleSetupHightouchTiktok(c)}>
                                          ⚡ Setup Hightouch → TikTok
                                        </Button>
                                      </div>
                                      {tiktokHtError[c.id] && (
                                        <div style={{ padding: '8px 12px', background: '#1a0a00', border: '1px solid #7c2d12', borderRadius: 7, fontSize: 11, color: '#f97316', lineHeight: 1.6 }}>
                                          {tiktokHtError[c.id]}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}

                              {s.key === 'twitter-hightouch' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                  <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>
                                    Auto-creates the Hightouch model + X Ads Custom Audience sync for account <span style={{ fontFamily: 'DM Mono, monospace', color: '#60a5fa' }}>{(c as any).twitterAccountId}</span>.
                                  </div>
                                  {htSetupTwitter?.clientId === c.id ? (
                                    <div style={{ color: '#4a5d7a', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <Spinner size={14} /> Creating Hightouch model &amp; sync for X Ads…
                                    </div>
                                  ) : (c as any).hightouchSyncIdTwitter ? (
                                    <div style={{ fontSize: 12, color: '#22c55e' }}>✓ Sync #{(c as any).hightouchSyncIdTwitter} active</div>
                                  ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                        <Button size="sm" style={{ color: '#f59e0b', borderColor: '#78350f' }} variant="ghost"
                                          onClick={() => handleSetupHightouchTwitter(c)}>
                                          ⚡ Setup Hightouch → X Ads
                                        </Button>
                                      </div>
                                      {twitterHtError[c.id] && (
                                        <div style={{ padding: '8px 12px', background: '#1a0a00', border: '1px solid #7c2d12', borderRadius: 7, fontSize: 11, color: '#f97316', lineHeight: 1.6 }}>
                                          {twitterHtError[c.id]}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}

                              {s.key === 'leadad' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                  <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.8 }}>
                                    <strong style={{ color: '#e2e8f0' }}>Yes — you can do this as their manager:</strong><br />
                                    Since they granted you partner access (Ad account + Page), you can create and launch Lead Ads entirely on their behalf. The form lives inside Meta — no landing page needed.
                                  </div>
                                  <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.8 }}>
                                    <strong style={{ color: '#e2e8f0' }}>Steps in Ads Manager:</strong><br />
                                    1. Switch to their ad account (top-left account switcher)<br />
                                    2. Create → Campaign objective: <strong style={{ color: '#e2e8f0' }}>Leads</strong><br />
                                    3. Ad level → <strong style={{ color: '#e2e8f0' }}>Instant Form</strong> → Create new form<br />
                                    4. Add fields: First name, Last name, Email, Phone (optional)<br />
                                    5. Intro headline + image → <strong style={{ color: '#e2e8f0' }}>Publish</strong><br />
                                    6. Export leads: Ads Manager → Leads Center → Download CSV
                                  </div>
                                  <div style={{ padding: '8px 12px', background: '#060a12', border: '1px solid #1a2840', borderRadius: 7, fontSize: 12, color: '#64748b' }}>
                                    Tip: Use "Higher volume" form type for lowest CPL. Use "Higher intent" (adds a review step) if lead quality is more important than volume.
                                  </div>
                                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                    {c.metaAdAccountId ? (
                                      <a
                                        href={`https://adsmanager.facebook.com/adsmanager/manage/campaigns?act=${c.metaAdAccountId.replace(/^act_/, '')}`}
                                        target="_blank" rel="noopener noreferrer"
                                        style={{ display: 'inline-block', padding: '6px 14px', background: '#1877f2', color: '#fff', borderRadius: 7, fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>
                                        Open Ads Manager →
                                      </a>
                                    ) : (
                                      <div style={{ fontSize: 11, color: '#ef4444' }}>Complete step 2 first to get the Ad Account ID</div>
                                    )}
                                    <Button size="sm" variant="ghost" onClick={() => handleMarkLeadAdForm(c)}>✓ Mark as created</Button>
                                  </div>
                                </div>
                              )}

                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* ── Audience Segments (admin) — always visible ─────────── */}
                <div style={{ borderTop: '1px solid #0d1a2e', padding: '14px 20px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <div style={{ fontSize: 11, color: '#3b4f6a', letterSpacing: '0.05em', fontFamily: 'DM Mono, monospace' }}>
                      AUDIENCE SEGMENTS
                    </div>
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => {
                        setSegmentModal({ clientId: c.id, clientName: c.clientName ?? '' });
                        if (!segmentsByClient[c.id]) loadSegmentsForClient(c.id);
                      }}
                      style={{ color: '#60a5fa', borderColor: 'rgba(96,165,250,0.3)', fontSize: 11 }}
                    >
                      + New Segment
                    </Button>
                  </div>
                  {!segmentsByClient[c.id] && (
                    <button
                      type="button"
                      onClick={() => loadSegmentsForClient(c.id)}
                      style={{ background: 'none', border: 'none', color: '#4a5d7a', fontSize: 11, cursor: 'pointer', padding: 0 }}
                    >
                      Load segments ↓
                    </button>
                  )}
                  {(segmentsByClient[c.id] ?? []).length === 0 && segmentsByClient[c.id] && (
                    <div style={{ fontSize: 12, color: '#3b4f6a', fontStyle: 'italic' }}>No segments yet.</div>
                  )}
                  {(segmentsByClient[c.id] ?? []).map((seg: any) => {
                    const statusColor: Record<string, string> = { active: '#34d399', pending_setup: '#fbbf24', paused: '#64748b', cancelled: '#ef4444' };
                    const col = statusColor[seg.status] ?? '#4a5d7a';
                    return (
                      <div key={seg.segmentId} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', marginBottom: 6, background: '#080c14', borderRadius: 8, border: `1px solid ${col}22` }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, color: '#e8edf5', fontSize: 13, marginBottom: 2 }}>{seg.segmentName}</div>
                          {seg.description && <div style={{ fontSize: 11, color: '#4a5d7a', marginBottom: 3 }}>{seg.description}</div>}
                          {seg.lastBilledAt && (
                            <div style={{ fontSize: 11, color: '#4a5d7a' }}>
                              Last billed {new Date(seg.lastBilledAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                              {seg.lastChargeAmount && seg.lastChargeAmount !== '0' ? ` · $${seg.lastChargeAmount}` : ' · $0 (no spend)'}
                            </div>
                          )}
                        </div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: col, textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0 }}>
                          {seg.status.replace('_', ' ')}
                        </div>
                        {seg.status !== 'cancelled' && (
                          <Button size="sm" variant="ghost" onClick={() => handlePauseSegment(c.id, seg.segmentId, seg.status)}
                            style={{ fontSize: 11, color: seg.status === 'paused' ? '#34d399' : '#fbbf24' }}>
                            {seg.status === 'paused' ? 'Resume' : 'Pause'}
                          </Button>
                        )}
                        {seg.status === 'cancelled' ? (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => handlePauseSegment(c.id, seg.segmentId, 'cancelled')}
                              style={{ fontSize: 11, color: '#60a5fa' }}>
                              Re-enable
                            </Button>
                            <Button size="sm" variant="danger" onClick={() => handleDeleteSegment(c.id, seg.segmentId, seg.segmentName)}>Delete</Button>
                          </>
                        ) : (seg.lastBilledAt || seg.status === 'active') ? (
                          <Button size="sm" variant="danger" onClick={() => handleDisableSegment(c.id, seg.segmentId, seg.segmentName)}
                            style={{ fontSize: 11 }}>
                            Disable
                          </Button>
                        ) : (
                          <Button size="sm" variant="danger" onClick={() => handleDeleteSegment(c.id, seg.segmentId, seg.segmentName)}>Del</Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>
            );
          })}
          </div>
        );
        })()
      )}

      {/* ── Audience Segment Creation Modal ──────────────────────────────── */}
      {segmentModal && (
        <Modal open onClose={() => setSegmentModal(null)} title={`New Audience Segment — ${segmentModal.clientName}`} width={500}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontSize: 13, color: '#8a9bb5', lineHeight: 1.6 }}>
              Create an audience segment card that the client will see in their portal. They'll click it to set up autopay. Charges run on the 1st of each month at <strong style={{ color: '#60a5fa' }}>30% of their Meta ad spend</strong>. No charge if they didn't spend.
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: '#8a9bb5', letterSpacing: '0.04em', display: 'block', marginBottom: 5 }}>Segment name *</label>
              <input
                value={newSegName}
                onChange={e => setNewSegName(e.target.value)}
                placeholder="e.g. Restaurant Owners NYC"
                autoFocus
                style={{ width: '100%', background: '#080c14', color: '#e8edf5', border: '1px solid #1e2d42', borderRadius: 7, padding: '9px 12px', fontSize: 13, fontFamily: 'Sora, sans-serif', outline: 'none', boxSizing: 'border-box' as const }}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: '#8a9bb5', letterSpacing: '0.04em', display: 'block', marginBottom: 5 }}>Description (optional)</label>
              <textarea
                value={newSegDesc}
                onChange={e => setNewSegDesc(e.target.value)}
                placeholder="e.g. Lookalike audience targeting restaurant owners in the NYC metro area"
                rows={2}
                style={{ width: '100%', background: '#080c14', color: '#e8edf5', border: '1px solid #1e2d42', borderRadius: 7, padding: '8px 12px', fontSize: 13, fontFamily: 'Sora, sans-serif', resize: 'vertical', outline: 'none' }}
              />
            </div>
            <div style={{ background: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.2)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#93c5fd', lineHeight: 1.5 }}>
              💡 After creating, expand this client's card → Segments section to see it. Notify your client to log into their portal to activate autopay.
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 4 }}>
              <Button variant="ghost" onClick={() => setSegmentModal(null)}>Cancel</Button>
              <Button variant="primary" loading={savingSeg} onClick={handleCreateSegment} disabled={!newSegName.trim()}>
                Create Segment
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Create / Edit Modal ───────────────────────────────────────────── */}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? 'Edit Client' : 'Add Client'}
        width={700}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

          {/* ── 1. Client Info ─────────────────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
            <Input label="Client name *" value={form.clientName} onChange={f('clientName')} placeholder="Acme Corp" />
            <Input label="Contact email" value={form.contactEmail} onChange={f('contactEmail')} placeholder="ops@client.com" />
            <Input label="Website" value={form.website} onChange={f('website')} placeholder="client.com" />
            <Input label="Industry" value={form.industry} onChange={f('industry')} placeholder="Healthcare" />
          </div>

          <div style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#fbbf24', lineHeight: 1.5 }}>
            ⚠️ <strong>Contact email</strong> must exactly match what the client uses to sign up at intentflo.com — otherwise their portal will show nothing.
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: '#8a9bb5', letterSpacing: '0.04em', display: 'block', marginBottom: 5 }}>Business Type</label>
              <select value={form.clientType} onChange={e => setForm(p => ({ ...p, clientType: e.target.value }))}
                style={{ width: '100%', background: '#080c14', color: form.clientType ? '#e8edf5' : '#4a5d7a', border: `1px solid ${form.clientType ? '#1e2d42' : 'rgba(251,191,36,0.4)'}`, borderRadius: 7, padding: '9px 12px', fontSize: 13, fontFamily: 'Sora, sans-serif', outline: 'none' }}>
                <option value="" disabled>Select business type…</option>
                <option value="b2b">B2B — credits / pay per contact</option>
                <option value="b2c">B2C — monthly retainer</option>
              </select>
            </div>
            {form.clientType === 'b2c' && (
              <div>
                <label style={{ fontSize: 12, fontWeight: 500, color: '#8a9bb5', letterSpacing: '0.04em', display: 'block', marginBottom: 5 }}>Billing Status</label>
                <select value={form.billingStatus} onChange={e => setForm(p => ({ ...p, billingStatus: e.target.value }))}
                  style={{ width: '100%', background: '#080c14', color: '#e8edf5', border: '1px solid #1e2d42', borderRadius: 7, padding: '9px 12px', fontSize: 13, fontFamily: 'Sora, sans-serif', outline: 'none' }}>
                  <option value="current">Current</option>
                  <option value="paid">Paid</option>
                  <option value="past_due">Past Due</option>
                  <option value="outstanding">Outstanding</option>
                </select>
              </div>
            )}
            {form.clientType === 'b2c' && (
              <div>
                <label style={{ fontSize: 12, fontWeight: 500, color: '#8a9bb5', letterSpacing: '0.04em', display: 'block', marginBottom: 5 }}>Balance Due ($)</label>
                <input type="number" min="0" step="0.01" value={form.balanceDue} onChange={e => setForm(p => ({ ...p, balanceDue: e.target.value }))} placeholder="0.00"
                  style={{ width: '100%', background: '#080c14', color: '#e8edf5', border: '1px solid #1e2d42', borderRadius: 7, padding: '9px 12px', fontSize: 13, fontFamily: 'Sora, sans-serif', outline: 'none', boxSizing: 'border-box' }} />
              </div>
            )}
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: '#8a9bb5', letterSpacing: '0.04em', display: 'block', marginBottom: 5 }}>Notes</label>
            <textarea value={form.notes} onChange={f('notes')} placeholder="Internal notes about this client..." rows={2}
              style={{ width: '100%', background: '#080c14', color: '#e8edf5', border: '1px solid #1e2d42', borderRadius: 7, padding: '8px 12px', fontSize: 13, fontFamily: 'Sora, sans-serif', resize: 'vertical', outline: 'none' }} />
          </div>

          {/* ── 2. Meta Ads ────────────────────────────────────────────── */}
          <div style={{ padding: '14px 16px', background: '#080c14', borderRadius: 8, border: '1px solid #111927' }}>
            <div style={{ fontSize: 11, color: '#4a5d7a', fontWeight: 600, letterSpacing: '0.06em', marginBottom: 12 }}>🟦 META ADS</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 14 }}>
              <Input label="Ad Account ID" value={form.metaAdAccountId} onChange={f('metaAdAccountId')} placeholder="act_123456789" helper="Client fills via onboarding link" />
              <Input label="Pixel ID" value={form.metaPixelId} onChange={f('metaPixelId')} placeholder="987654321" helper="From Meta Events Manager" />
              <Input label="System User Token" value={form.metaPageAccessToken} onChange={f('metaPageAccessToken')} placeholder="EAABsbCS..." helper="BM → Settings → Users → System Users → Add → Generate Token → ads_read + ads_management" />
              <Input label="CAPI Token (Secrets ARN)" value={form.metaCapiTokenArn} onChange={f('metaCapiTokenArn')} placeholder="audience-platform/meta-capi/CLIENT_ID" helper="Secrets Manager path for CAPI token" />
              <Input label="Page ID" value={form.metaPageId} onChange={f('metaPageId')} placeholder="123456789" helper="From Facebook Page URL or Meta Business Suite" />
              <Input label="Lead Form ID(s)" value={form.metaLeadFormId} onChange={f('metaLeadFormId')} placeholder="987654321, 123456789" helper="Comma-separated for multiple forms" />
              <Input label="Lead notification email" value={form.notificationEmail} onChange={f('notificationEmail')} placeholder="sales@client.com" helper="Instant email alerts on new leads" />
              <Input label="Lead notification SMS" value={form.notificationPhone} onChange={f('notificationPhone')} placeholder="+15551234567, +12025551234" helper="Comma-separated numbers for lead SMS alerts" />
              <div>
                <label style={{ fontSize: 12, fontWeight: 500, color: '#8a9bb5', letterSpacing: '0.04em', display: 'block', marginBottom: 5 }}>Auto-call leads (Retell AI)</label>
                <button type="button" onClick={() => setForm(p => ({ ...p, retellEnabled: p.retellEnabled === 'true' ? 'false' : 'true' }))}
                  style={{ background: form.retellEnabled === 'true' ? '#0d2a4a' : '#080c14', color: form.retellEnabled === 'true' ? '#5ba3f5' : '#4a5d7a', border: '1px solid ' + (form.retellEnabled === 'true' ? '#1e4a7a' : '#1e2d42'), borderRadius: 7, padding: '7px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'Sora, sans-serif' }}>
                  {form.retellEnabled === 'true' ? '✓ Enabled' : 'Disabled'}
                </button>
                <div style={{ fontSize: 11, color: '#4a5d7a', marginTop: 4 }}>Calls the lead via Retell AI the moment a Meta lead comes in</div>
              </div>
            </div>
          </div>

          {/* ── 3. Other Platforms (collapsible) ───────────────────────── */}
          <div>
            <button type="button" onClick={() => setShowAdvPlatforms(p => !p)}
              style={{ background: 'none', border: '1px solid #111927', borderRadius: 8, padding: '8px 16px', color: '#4a5d7a', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'Sora, sans-serif', width: '100%', textAlign: 'left', letterSpacing: '0.04em' }}>
              {showAdvPlatforms ? '▴ Hide' : '▾ Show'} other platforms &amp; advanced settings
            </button>
            {showAdvPlatforms && (
              <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ padding: '12px 14px', background: '#080c14', borderRadius: 8, border: '1px solid #111927' }}>
                  <div style={{ fontSize: 11, color: '#4a5d7a', fontWeight: 600, letterSpacing: '0.06em', marginBottom: 10 }}>🔍 GOOGLE ADS</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
                    <Input label="Customer ID" value={form.googleCustomerId} onChange={f('googleCustomerId')} placeholder="1234567890" helper="10-digit Google Ads Customer ID" />
                    <Input label="Hightouch Sync ID" value={form.hightouchSyncIdGoogle} onChange={f('hightouchSyncIdGoogle')} placeholder="12345" helper="Auto-filled after Setup Hightouch" />
                  </div>
                </div>
                <div style={{ padding: '12px 14px', background: '#080c14', borderRadius: 8, border: '1px solid #111927' }}>
                  <div style={{ fontSize: 11, color: '#4a5d7a', fontWeight: 600, letterSpacing: '0.06em', marginBottom: 10 }}>💼 LINKEDIN ADS</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
                    <Input label="Account ID" value={form.linkedinAccountId} onChange={f('linkedinAccountId')} placeholder="123456789" helper="Campaign Manager account ID (from URL)" />
                    <Input label="Hightouch Sync ID" value={form.hightouchSyncIdLinkedin} onChange={f('hightouchSyncIdLinkedin')} placeholder="12345" helper="Auto-filled after Setup Hightouch" />
                  </div>
                </div>
                <div style={{ padding: '12px 14px', background: '#080c14', borderRadius: 8, border: '1px solid #111927' }}>
                  <div style={{ fontSize: 11, color: '#4a5d7a', fontWeight: 600, letterSpacing: '0.06em', marginBottom: 10 }}>🎵 TIKTOK ADS</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
                    <Input label="Advertiser ID" value={form.tiktokAdvertiserId} onChange={f('tiktokAdvertiserId')} placeholder="7012345678901234567" helper="From TikTok Ads Manager account switcher" />
                    <Input label="Hightouch Sync ID" value={form.hightouchSyncIdTiktok} onChange={f('hightouchSyncIdTiktok')} placeholder="12345" helper="Auto-filled after Setup Hightouch" />
                  </div>
                </div>
                <div style={{ padding: '12px 14px', background: '#080c14', borderRadius: 8, border: '1px solid #111927' }}>
                  <div style={{ fontSize: 11, color: '#4a5d7a', fontWeight: 600, letterSpacing: '0.06em', marginBottom: 10 }}>✖️ X (TWITTER) ADS</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
                    <Input label="Account ID" value={form.twitterAccountId} onChange={f('twitterAccountId')} placeholder="abc1234" helper="From URL: ads.x.com/accounts/YOUR_ID" />
                    <Input label="Hightouch Sync ID" value={form.hightouchSyncIdTwitter} onChange={f('hightouchSyncIdTwitter')} placeholder="12345" helper="Auto-filled after Setup Hightouch" />
                  </div>
                </div>
                <div style={{ padding: '12px 14px', background: '#080c14', borderRadius: 8, border: '1px solid #111927' }}>
                  <div style={{ fontSize: 11, color: '#4a5d7a', fontWeight: 600, letterSpacing: '0.06em', marginBottom: 10 }}>⚙️ AUDIENCE SYNC IDs (HIGHTOUCH)</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
                    <Input label="Pixel audience sync ID" value={form.hightouchSyncIdPixel} onChange={f('hightouchSyncIdPixel')} placeholder="12345" helper="From Hightouch → Syncs URL" />
                    <Input label="Prospeo sync ID" value={form.hightouchSyncIdProspeo} onChange={f('hightouchSyncIdProspeo')} placeholder="67890" helper="Used as lookalike seed only" />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── IntentFlo Business IDs ──────────────────────────────────── */}
          <div style={{ padding: '12px 14px', background: '#080c14', borderRadius: 8, border: '1px solid #111927' }}>
            <div style={{ fontSize: 11, color: '#4a5d7a', fontWeight: 600, letterSpacing: '0.06em', marginBottom: 10 }}>
              INTENTFLO BUSINESS IDs — share with clients to grant ad platform access
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
              {[
                { label: 'Meta Business Portfolio', id: '908181335430042' },
                { label: 'Google MCC',              id: '665-481-1776' },
                { label: 'LinkedIn Business',       id: '7449190895490445312' },
                { label: 'TikTok Business Center',  id: '7627972506850279441' },
                { label: 'X Ads Account',           id: '18ce55vcwpp' },
              ].map(p => (
                <div key={p.label} style={{ background: '#060a10', border: '1px solid #0d1a2e', borderRadius: 7, padding: '8px 12px' }}>
                  <div style={{ fontSize: 10, color: '#4a5d7a', marginBottom: 4 }}>{p.label}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 11, color: '#60a5fa' }}>{p.id}</span>
                    <button type="button" onClick={() => navigator.clipboard.writeText(p.id)} title="Copy"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4a5d7a', padding: 0, fontSize: 12 }}>⎘</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 4 }}>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="primary" loading={saving} onClick={handleSave}>
              {editing ? 'Save Changes' : 'Create Client'}
            </Button>
          </div>
        </div>
      </Modal>

      {ToastEl}

      {/* ── Trial Subscription Modal ─────────────────────────────────────── */}
      {trialSubModal && (
        <Modal open={!!trialSubModal} onClose={() => { setTrialSubModal(null); setTrialSubUrl(null); }}
          title={`Send Subscription Link — ${trialSubModal.clientName}`} width={500}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontSize: 13, color: '#8a9bb5', lineHeight: 1.65 }}>
              Send {trialSubModal.clientName} a payment link to continue services after their 14-day trial.
              Once they pay, they'll be moved from <strong style={{ color: '#818cf8' }}>Trial Users</strong> to <strong style={{ color: '#60a5fa' }}>Clients</strong> automatically.
            </div>

            {/* Plan options */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {([
                { id: 'monthly', label: '$300 / month', sub: 'Billed monthly — most popular',  badge: '$300/mo' },
                { id: 'weekly',  label: '$70 / week',   sub: 'Billed weekly — flexible option', badge: '$70/wk'  },
              ] as const).map(p => (
                <label key={p.id} style={{
                  display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px',
                  background: trialSubPlan === p.id ? 'rgba(59,130,246,0.08)' : '#080c14',
                  border:    `1px solid ${trialSubPlan === p.id ? 'rgba(59,130,246,0.4)' : '#1e2d42'}`,
                  borderRadius: 10, cursor: 'pointer', transition: 'all 0.15s',
                }}>
                  <input type="radio" name="trialPlan" value={p.id}
                    checked={trialSubPlan === p.id}
                    onChange={() => setTrialSubPlan(p.id)}
                    style={{ accentColor: '#3b82f6', width: 16, height: 16, flexShrink: 0 }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, color: '#e8edf5', fontSize: 15 }}>{p.label}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{p.sub}</div>
                  </div>
                  <div style={{
                    fontWeight: 800, fontSize: 14, padding: '4px 12px', borderRadius: 8,
                    background: trialSubPlan === p.id ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.05)',
                    color: trialSubPlan === p.id ? '#60a5fa' : '#4a5d7a',
                  }}>{p.badge}</div>
                </label>
              ))}
            </div>

            {/* Email context */}
            {trialSubModal.contactEmail ? (
              <div style={{ background: 'rgba(52,211,153,0.07)', border: '1px solid rgba(52,211,153,0.2)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#34d399' }}>
                Sending to: <strong>{trialSubModal.contactEmail}</strong>
              </div>
            ) : (
              <div style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#fbbf24' }}>
                ⚠️ No email on file. Add one in Edit Client to generate the link.
              </div>
            )}

            {/* Generated link display */}
            {trialSubUrl && (
              <div style={{ background: '#060a12', border: '1px solid #1a2840', borderRadius: 8, padding: '12px 16px' }}>
                <div style={{ fontSize: 10, color: '#4a5d7a', fontWeight: 600, letterSpacing: '0.06em', marginBottom: 6 }}>PAYMENT LINK — copied to clipboard ✓</div>
                <div style={{ fontSize: 11, color: '#60a5fa', fontFamily: 'DM Mono, monospace', wordBreak: 'break-all', lineHeight: 1.6, marginBottom: 10 }}>{trialSubUrl}</div>
                <button type="button"
                  onClick={() => { navigator.clipboard.writeText(trialSubUrl!); show('Link copied!'); }}
                  style={{ background: 'none', border: '1px solid #1a2840', borderRadius: 5, color: '#4a5d7a', fontSize: 11, cursor: 'pointer', padding: '4px 10px', fontFamily: 'Sora, sans-serif' }}>
                  ⎘ Copy again
                </button>
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
              <Button variant="ghost" onClick={() => { setTrialSubModal(null); setTrialSubUrl(null); }}>
                {trialSubUrl ? 'Close' : 'Cancel'}
              </Button>
              <Button
                variant="primary"
                loading={trialSubLoading}
                onClick={handleGetTrialSubscriptionLink}
                disabled={!trialSubModal.contactEmail}
                style={{ background: '#818cf8', borderColor: '#818cf8' }}
              >
                {trialSubUrl ? '↻ Regenerate Link' : '💳 Get Payment Link'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Send Onboarding Modal ────────────────────────────────────────── */}
      {onboardModal && (
        <Modal open={!!onboardModal} onClose={() => setOnboardModal(null)} title={`Send Onboarding — ${onboardModal.clientName}`} width={480}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {!onboardModal.contactEmail && (
              <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#ef4444' }}>
                No email on file for this client. Add one in Edit Client first.
              </div>
            )}
            {onboardModal.contactEmail && (
              <div style={{ background: 'rgba(52,211,153,0.07)', border: '1px solid rgba(52,211,153,0.2)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#34d399' }}>
                Will send to: <strong>{onboardModal.contactEmail}</strong>
              </div>
            )}
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: '#8a9bb5', display: 'block', marginBottom: 5 }}>Plan</label>
              <input
                value={onboardPlan} onChange={e => setOnboardPlan(e.target.value)}
                placeholder="e.g. Growth — $4,000/month"
                style={{ width: '100%', background: '#080c14', color: '#e8edf5', border: '1px solid #1e2d42', borderRadius: 7, padding: '9px 12px', fontSize: 13, fontFamily: 'Sora, sans-serif', outline: 'none', boxSizing: 'border-box' as const }}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: '#8a9bb5', display: 'block', marginBottom: 5 }}>Platforms</label>
              <input
                value={onboardPlats} onChange={e => setOnboardPlats(e.target.value)}
                placeholder="e.g. Meta, Google"
                style={{ width: '100%', background: '#080c14', color: '#e8edf5', border: '1px solid #1e2d42', borderRadius: 7, padding: '9px 12px', fontSize: 13, fontFamily: 'Sora, sans-serif', outline: 'none', boxSizing: 'border-box' as const }}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: '#8a9bb5', display: 'block', marginBottom: 5 }}>Monthly Ad Budget Target</label>
              <input
                value={onboardBudget} onChange={e => setOnboardBudget(e.target.value)}
                placeholder="e.g. $5,000/month"
                style={{ width: '100%', background: '#080c14', color: '#e8edf5', border: '1px solid #1e2d42', borderRadius: 7, padding: '9px 12px', fontSize: 13, fontFamily: 'Sora, sans-serif', outline: 'none', boxSizing: 'border-box' as const }}
              />
            </div>
            <p style={{ fontSize: 11, color: '#3b4f6a', margin: 0, lineHeight: 1.6 }}>
              Email includes: billing/autopay link, Meta partner ID (908181335430042), tracking + asset checklist, approval workflow reminder.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
              <Button variant="ghost" onClick={() => setOnboardModal(null)}>Cancel</Button>
              <Button variant="primary" loading={onboardSending} onClick={handleSendOnboarding} disabled={!onboardModal.contactEmail}>
                Send Onboarding Email
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Send Bill Modal ─────────────────────────────────────────────── */}
      {billModal && (
        <Modal open={!!billModal} onClose={() => setBillModal(null)} title={`Send Bill — ${billModal.clientName}`} width={480}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: '#8a9bb5', display: 'block', marginBottom: 5 }}>
                Amount Due ($) *
              </label>
              <input
                type="number" min="0" step="0.01"
                value={billAmount}
                onChange={e => setBillAmount(e.target.value)}
                placeholder="500.00"
                style={{
                  width: '100%', background: '#080c14', color: '#e8edf5',
                  border: '1px solid #1e2d42', borderRadius: 7,
                  padding: '9px 12px', fontSize: 14, fontFamily: 'Sora, sans-serif',
                  outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: '#8a9bb5', display: 'block', marginBottom: 5 }}>
                Description
              </label>
              <input
                value={billDesc}
                onChange={e => setBillDesc(e.target.value)}
                style={{
                  width: '100%', background: '#080c14', color: '#e8edf5',
                  border: '1px solid #1e2d42', borderRadius: 7,
                  padding: '9px 12px', fontSize: 13, fontFamily: 'Sora, sans-serif',
                  outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#8a9bb5' }}>
                <input type="checkbox" checked={billEmail} onChange={e => setBillEmail(e.target.checked)}
                  style={{ accentColor: '#3b82f6', width: 14, height: 14 }} />
                Send via email
                {billEmail && billModal.contactEmail && (
                  <span style={{ fontSize: 11, color: '#4a5d7a' }}>→ {billModal.contactEmail}</span>
                )}
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#8a9bb5' }}>
                <input type="checkbox" checked={billSms} onChange={e => setBillSms(e.target.checked)}
                  style={{ accentColor: '#3b82f6', width: 14, height: 14 }} />
                Send via SMS
              </label>
            </div>

            {billSms && (
              <div>
                <label style={{ fontSize: 12, fontWeight: 500, color: '#8a9bb5', display: 'block', marginBottom: 5 }}>
                  Phone number
                </label>
                <input
                  type="tel"
                  value={billPhone}
                  onChange={e => setBillPhone(e.target.value)}
                  placeholder="+15551234567"
                  style={{
                    width: '100%', background: '#080c14', color: '#e8edf5',
                    border: '1px solid #1e2d42', borderRadius: 7,
                    padding: '9px 12px', fontSize: 13, fontFamily: 'Sora, sans-serif',
                    outline: 'none', boxSizing: 'border-box',
                  }}
                />
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
              <Button variant="ghost" onClick={() => setBillModal(null)}>Cancel</Button>
              <Button variant="primary" loading={billSending} onClick={handleSendBill}>
                Send Bill
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {autoChargeModal && (() => {
        const monthlyMin  = parseFloat((autoChargeModal as any).monthlyMin ?? '399');
        const actualSpend = parseFloat(autoChargeSpend);
        const calculated  = isNaN(actualSpend) ? monthlyMin : Math.max(actualSpend * 0.30, monthlyMin);
        const chargeAmt   = Math.round(calculated * 100) / 100;
        const hasCard     = !!(autoChargeModal as any).defaultPaymentMethodId;
        return (
          <Modal open={!!autoChargeModal} onClose={() => { setAutoChargeModal(null); setAutoChargeResult(null); }}
            title={`Auto-charge — ${autoChargeModal.clientName}`} width={420}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {!hasCard && (
                <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#f87171', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div>⚠️ No saved card on file. Client must complete checkout first before auto-charging.</div>
                  <Button size="sm" variant="ghost" onClick={() => handleCopyAutopayLink(autoChargeModal)}
                    style={{ color: '#38bdf8', borderColor: 'rgba(56,189,248,0.3)', alignSelf: 'flex-start' }}>
                    🔗 Copy Autopay Link
                  </Button>
                </div>
              )}
              <div style={{ background: '#0a1628', borderRadius: 8, padding: '12px 14px', fontSize: 13 }}>
                <div style={{ color: '#64748b', marginBottom: 4 }}>Billing minimum</div>
                <div style={{ color: '#a78bfa', fontWeight: 700, fontSize: 16 }}>
                  ${monthlyMin.toFixed(2)}/mo
                  {(autoChargeModal as any).promoCode && <span style={{ fontSize: 11, color: '#7c3aed', marginLeft: 6 }}>({(autoChargeModal as any).promoCode})</span>}
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 500, color: '#8a9bb5', display: 'block', marginBottom: 5 }}>
                  Actual ad spend this month ($) — leave blank to charge minimum only
                </label>
                <input
                  type="number"
                  min="0"
                  value={autoChargeSpend}
                  onChange={e => setAutoChargeSpend(e.target.value)}
                  placeholder={`${(autoChargeModal as any).monthlyAdSpend ?? ''} (budget)`}
                  style={{
                    width: '100%', background: '#080c14', color: '#e8edf5',
                    border: '1px solid #1e2d42', borderRadius: 7,
                    padding: '9px 12px', fontSize: 13, fontFamily: 'Sora, sans-serif',
                    outline: 'none', boxSizing: 'border-box',
                  }}
                />
              </div>
              <div style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 8, padding: '10px 14px', fontSize: 13 }}>
                <span style={{ color: '#64748b' }}>Charge amount: </span>
                <strong style={{ color: '#818cf8', fontSize: 15 }}>${chargeAmt.toFixed(2)}</strong>
                {!isNaN(actualSpend) && actualSpend > 0 && (
                  <span style={{ color: '#475569', fontSize: 11, marginLeft: 8 }}>
                    = max(30% × ${actualSpend.toFixed(0)}, min ${monthlyMin})
                  </span>
                )}
              </div>
              {autoChargeResult && (
                <div style={{
                  background: autoChargeResult.ok ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                  border: `1px solid ${autoChargeResult.ok ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
                  borderRadius: 8, padding: '10px 14px', fontSize: 13,
                  color: autoChargeResult.ok ? '#34d399' : '#f87171',
                }}>
                  {autoChargeResult.msg}
                </div>
              )}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
                <Button variant="ghost" onClick={() => { setAutoChargeModal(null); setAutoChargeResult(null); }}>
                  {autoChargeResult?.ok ? 'Close' : 'Cancel'}
                </Button>
                {!autoChargeResult?.ok && (
                  <Button variant="primary" loading={autoChargeLoading} onClick={handleAutoCharge}
                    style={{ background: '#6366f1', borderColor: '#6366f1' }}
                    disabled={!hasCard}>
                    💳 Charge ${chargeAmt.toFixed(2)}
                  </Button>
                )}
              </div>
            </div>
          </Modal>
        );
      })()}
    </div>
  );
}


